// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import {
  BrowserApi,
  ColorArray,
  Cookie,
  CookieStore,
  Listener,
  Manifest,
  MessageSender,
  Port,
  Tab
} from "@lib/browser/BrowserApi";

export type Callback<T> = (result: T) => void;

/**
 * Declares the chrome global variable in order to use the chrome API in a type safe manner.
 * @see [Chrome developer guide](https://developer.chrome.com/extensions/devguide)
 */
declare var chrome: {
  windows: {
    get(id: number): Promise<Window>;
    update(id: number, updateOptions: { focused: boolean; drawAttention: boolean }): void;
  };

  action: {
    disable(tabID: Optional<number>, callback: Callback<void>): void;
    enable(tabID: Optional<number>, callback: Callback<void>): void;
    setBadgeBackgroundColor(details: {
      color: ColorArray,
      tabId?: number
    }, callback: Callback<void>): void;
    setBadgeText(details: {
      text: Nullable<string>,
      tabId?: number
    }, callback: Callback<void>): void;
    setIcon(details: {
      imageData?: ImageData | { [key: number]: ImageData };
      path?: string | { [key: number]: string };
      tabId?: number;
    }, callback: Callback<void>): void;
    setTitle(details: {
      title: Nullable<string>,
      tabId?: number
    }, callback: Callback<void>): void;
  };

  cookies: {
    getAll(details: {}, callback: Callback<Cookie[]>): void;
    getAllCookieStores(callback: Callback<CookieStore[]>): void;
  };

  runtime: {
    readonly lastError: null | string;
    readonly id: string;

    onConnect: Listener<(port: Port) => void>;
    onMessage: Listener<(message: object, sender: MessageSender, sendResponse: (result?: object) => void) => boolean>;

    connect(extensionID?: string, connectInfo?: {
      name?: string
    }): Port;
    connectNative(application: string): Port;
    getManifest(): Manifest;
    sendMessage(message: any, callback: Callback<any>): Promise<any>;
  };
  
  scripting: {
    executeScript(injection: {
      args?: any[],
      files?: string[],
      injectImmediately?: boolean,
      target: {
        allFrames?: boolean,
        documentIds?: string,
        frameIds?: number[],
        tabId: number
      },
      world?: "ISOLATED" | "MAIN",
      func?: void
    }, callback: Callback<any[]>): Promise<any[]>;
  };
  
  tabs: {
    get(tabID: number, callback: Callback<Tab>): void;
    reload(tabId: number): void;
    query(queryOptions: { url: string }): Promise<Tab[]>;
  };

  storage: {
    local: {
      set(items: { [key: string]: any }): Promise<void>;
      get(keys?: string | string[] | { [key: string]: any } | null): Promise<{ [key: string]: any }>;
      remove(keys: string | string[]): Promise<void>;
    },
    managed: {
      get<T>(key: string): Promise<{ [key: string]: any }>;
    }
  };
  
  alarms: {
    create(name: string, alarmInfo: {delayInMinutes?: number, periodInMinutes?: number, when?: number}, callback?: Function): Promise<any>;
    onAlarm: Listener<(alarm: { name: string, periodInMinutes?: number, scheduledTime: number}) => void>;
  }
};

type AllButLastParameter<T extends any[]> = T extends [...infer TRestParameters, any] ? TRestParameters : [];

export function makeApiCall<TParameters extends any[]>(target: (...args: TParameters) => any, ...args: AllButLastParameter<TParameters>): Promise<any>
{
  if (!target)
    throw new Error("Invalid browser API call.");

  return new Promise((resolve, reject) =>
  {
    (target as (...args: any[]) => any)(...args, (result: any) =>
    {
      if (chrome.runtime.lastError !== undefined)
        reject(chrome.runtime.lastError);
      else
        resolve(result);
    });
  });
}

/**
 * Provides an implementation of the BrowserApi for chrome.
 * Converts the callback based methods to the required promise based form.
 */
export const ChromeBrowser: BrowserApi = {
  browserAction: {
    disable(tabID)
    {
      return makeApiCall(chrome.action.disable, tabID);
    },
    enable(tabID)
    {
      return makeApiCall(chrome.action.enable, tabID);
    },
    setBadgeBackgroundColor(details)
    {
      return makeApiCall(chrome.action.setBadgeBackgroundColor, details);
    },
    setIcon(details)
    {
      return makeApiCall(chrome.action.setIcon, details);
    },
    setBadgeText(details)
    {
      if (details.text === null)
        details.text = "";

      return makeApiCall(chrome.action.setBadgeText, details);
    },
    setBadgeTextColor(details) 
    {
      // Chrome has no support for a text color so this is a no-op.
      return Promise.resolve();
    },
    setTitle(details)
    {
      if (details.title === null)
        details.title = "";

      return makeApiCall(chrome.action.setTitle, details);
    }
  },
  cookies: {
    getAll(details)
    {
      return makeApiCall(chrome.cookies.getAll, details);
    },
    getAllCookieStores()
    {
      return makeApiCall(chrome.cookies.getAllCookieStores);
    }
  },
  runtime: {
    get id(): string
    {
      return chrome.runtime.id;
    },
    get lastError(): Nullable<string>
    {
      return chrome.runtime.lastError;
    },
    onConnect: {
      addListener(callback)
      {
        chrome.runtime.onConnect.addListener(callback);
      },
      removeListener(callback)
      {
        chrome.runtime.onConnect.removeListener(callback);
      }
    },
    onMessage: {
      addListener(callback)
      {
        chrome.runtime.onMessage.addListener((message, sender, sendResponse) =>
        {
          const result = callback(message, sender);
          let responseSent = false;
          if (result && typeof result === "object" && typeof result.then === "function")
          {
            result.then(result =>
            {
              if (!responseSent)
              {
                responseSent = true;
                sendResponse(result);
              }
            }, () =>
            {
              if (!responseSent)
              {
                responseSent = true;
                sendResponse();
              }
            });
          }
          else
          {
            sendResponse(result);
          }

          return true;
        });
      },
      removeListener(callback)
      {
      }
    },
    connect(extensionID, connectInfo): Port
    {
      return chrome.runtime.connect(extensionID, connectInfo);
    },
    connectNative(application): Port
    {
      return chrome.runtime.connectNative(application);
    },
    getManifest(): Manifest
    {
      return chrome.runtime.getManifest();
    },
    sendMessage(message: any): Promise<any>
    {
      return makeApiCall(chrome.runtime.sendMessage, message);
    }
  },
  tabs: {
    executeScript(tabID, details): Promise<any[]>
    {
        return makeApiCall(
        chrome.scripting.executeScript,
        {
         files: details.file == null ? undefined : [details.file],
         target: {allFrames: details.allFrames, frameIds: details.frameId == null ? undefined : [details.frameId], tabId: tabID}
        });
    },
    getTab(tabID): Promise<Tab>
    {
      return makeApiCall(chrome.tabs.get, tabID);
    },
    reload(tabID: number) {
      return chrome.tabs.reload(tabID);
    },
    query(queryOptions: { url: string }): Promise<Tab[]> {
      return chrome.tabs.query(queryOptions);
    }
  },
  storage: {
    local: {
      async get(key: string): Promise<any> {
        let result = await chrome.storage.local.get(key);
        if (key in result)
          return result[key];
        return null;
      },
      set(items: { [p: string]: any }): Promise<void> {
        return chrome.storage.local.set(items);
      },
      remove(key: string): Promise<void> {
        return chrome.storage.local.remove(key);
      }
    },
    managed: {
      async get<T>(key: string): Promise<T | null> {
        let result = await chrome.storage.managed.get(key);
        if (key in result && result[key] as T) {
          return result[key] as T;
        }
        return null;
      }
    }
  },
  windows: {
    get(id: number): Promise<Window> {
      return chrome.windows.get(id);
    },
    update(id: number, updateOptions: { focused: boolean; drawAttention: boolean }) {
      return chrome.windows.update(id, updateOptions);
    }
  },
  alarms: {
    create(name: string, alarmInfo: {delayInMinutes?: number, periodInMinutes?: number, when?: number}, callback?: Function): Promise<any> {
      return chrome.alarms.create(name, alarmInfo, callback);
    },
    onAlarm: {      
      addListener(callback)
      {
        chrome.alarms.onAlarm.addListener(callback);
      },
      removeListener(callback)
      {
        chrome.alarms.onAlarm.removeListener(callback);
      }
    }
  }
};